import React, { useState, useEffect, useContext, useCallback } from "react";
import { Modal, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import { ErroModal } from "../Generals";
import api, { BASE_URL } from "../../Utils/api";
import { calcularProximaDataExecucao, formatarData, formatarDataISO } from "../../Utils/calcularProximaDataExecucao";
import { getDescricaoPeriodicidade } from "../../Utils/dadosAgendamento";
import { GlobalContext } from "../../Utils/GlobalContext";


const AgendamentoDocumentoModal = ({ show, handleClose, documento, tiposAgendamento, onAgendamentoAdded }) => {
  const [selectedTipoAgendamento, setSelectedTipoAgendamento] = useState("");
  const [dataProximaExecucao, setDataProximaExecucao] = useState("");
  const [horario, setHorario] = useState("08:00");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [camposEditaveis, setCamposEditaveis] = useState(true);
  const [loadingTipos, setLoadingTipos] = useState(false);
  const [tiposAgendamentoLocal, setTiposAgendamentoLocal] = useState([]);
  const [proximaDataDisplay, setProximaDataDisplay] = useState("");

  const token = localStorage.getItem("authToken");
  const { globalData } = useContext(GlobalContext);
  const entidadeID = globalData?.entidade_id || localStorage.getItem("entidade_id");

  // Memoizar resetForm para estabilidade
  const resetForm = useCallback(() => {
    setSelectedTipoAgendamento(documento?.agendamentoAtual?.tipo_agendamento_id || "");
    setDataProximaExecucao(formatarDataISO(new Date()));
    setHorario("08:00");
    setError(null);
    setCamposEditaveis(true);
    setProximaDataDisplay("");
  }, [documento]);

  // 1) Buscar tipos / resetar quando modal abre
  useEffect(() => {
    if (!show) return;

    resetForm();

    if (documento) {
      console.log("Documento no modal:", documento);
      console.log("Data de vencimento:", documento.dt_vencimento);
    }

    if (tiposAgendamento && tiposAgendamento.length > 0) {
      setTiposAgendamentoLocal(tiposAgendamento);
    } else {
      setLoadingTipos(true);
      api
        .get(`${BASE_URL}/api/tiposAgendamentos/ListarTiposAgendamento/${entidadeID}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          console.log("Tipos de agendamento carregados:", res.data);
          setTiposAgendamentoLocal(res.data.data);
        })
        .catch((err) => {
          console.error("Erro ao carregar tipos de agendamento:", err);
          setError("Não foi possível carregar os tipos de agendamento. Por favor, tente novamente.");
        })
        .finally(() => {
          setLoadingTipos(false);
        });
    }
  }, [show, tiposAgendamento, entidadeID, token, resetForm, documento]);

  // 2) Quando tipo muda, calcular próxima data
  useEffect(() => {
    if (!selectedTipoAgendamento) {
      setCamposEditaveis(true);
      setProximaDataDisplay("");
      return;
    }

    const tipoEncontrado = tiposAgendamentoLocal.find(
      (t) => t.id.toString() === selectedTipoAgendamento.toString()
    );
    if (!tipoEncontrado) return;

    const { data, horario: novoHorario, dataEditavel } = calcularProximaDataExecucao(tipoEncontrado, documento);
    setDataProximaExecucao(data);
    setHorario(novoHorario);
    setCamposEditaveis(dataEditavel);

    if (tipoEncontrado.periodicidade === "V" || tipoEncontrado.periodicidade === "VD") {
      const dataFormatada = formatarData(data);
      setProximaDataDisplay(
        dataFormatada
          ? `Próxima Execução: ${dataFormatada} ${novoHorario}`
          : "Não foi possível calcular a data de execução"
      );
    } else {
      setProximaDataDisplay("");
    }
  }, [selectedTipoAgendamento, tiposAgendamentoLocal, documento]);

  const validarFormulario = () => {
    if (!selectedTipoAgendamento) {
      setError("Selecione um tipo de agendamento");
      return false;
    }
    if (!dataProximaExecucao) {
      setError("Informe a data da próxima execução");
      return false;
    }
    if (!horario) {
      setError("Informe o horário da execução");
      return false;
    }
    const dataExecucao = new Date(`${dataProximaExecucao}T${horario}`);
    if (dataExecucao <= new Date()) {
      setError("A data e hora da próxima execução devem ser futuras");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validarFormulario()) return;

    setLoading(true);
    setError(null);
    try {
      const dataHoraExecucao = `${dataProximaExecucao}T${horario}:00`;
      await api.post(
        `${BASE_URL}/api/agendamentos/AssociarAgendamentoDocumento`,
        {
          id_documento: documento.id,
          id_tipo_agendamento: selectedTipoAgendamento,
          data_proxima_execucao: dataHoraExecucao,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onAgendamentoAdded) onAgendamentoAdded();
      handleClose();
    } catch (err) {
      console.error("Erro ao associar agendamento ao documento:", err);
      setError(err.response?.data?.message || "Ocorreu um erro ao associar o agendamento. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const formatarPeriodicidade = (p) => getDescricaoPeriodicidade(p);

  const tiposAgendamentoFiltrados = tiposAgendamentoLocal.filter((tipo) => tipo.periodicidade !== "U");

  if (!documento) return null;

  const dataVencimento = documento.dt_vencimento || documento.data_vencimento || documento.DATA_FINAL;
  const dataVencimentoFormatada = formatarData(dataVencimento);

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" dialogClassName="modal-dialog-top modal-90w" size="xl">
      <Modal.Header closeButton style={{ background: "var(--surface-sunken)", borderColor: "var(--border-default)", paddingBottom: 12 }}>
        <Modal.Title style={{ fontSize: "1rem", fontWeight: 600, color: "var(--text-body)" }}>Adicionar Agendamento ao Documento</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ background: "var(--surface-card)", color: "var(--text-body)" }}>
        <ErroModal erro={error} />
        <div className="mb-3">
          <h6 style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--text-body)", marginBottom: 8 }}>Documento:</h6>
          <p className="mb-1">
            <strong>Contrato:</strong> {documento.nr_contrato || documento.id}
          </p>
          <p className="mb-1">
            <strong>Tipo:</strong> {documento.tipo_documento || "Não especificado"}
          </p>
          <p className="mb-1">
            <strong>Fornecedor:</strong> {documento.fornecedor || "Não especificado"}
          </p>
          {dataVencimentoFormatada && (
            <p className="mb-0">
              <strong>Data de Vencimento:</strong> {dataVencimentoFormatada}
            </p>
          )}
        </div>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Tipo de Agendamento <span className="text-danger">*</span>
            </Form.Label>
            {loadingTipos ? (
              <div className="d-flex align-items-center">
                <Spinner size="sm" animation="border" className="me-2" />
                <span>Carregando tipos de agendamento...</span>
              </div>
            ) : (
              <>
                <Form.Select
                  value={selectedTipoAgendamento}
                  onChange={(e) => setSelectedTipoAgendamento(e.target.value)}
                  disabled={loading || tiposAgendamentoFiltrados.length === 0}
                  required
                >
                  <option value="">Selecione um tipo de agendamento</option>
                  {tiposAgendamentoFiltrados.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nome} ({formatarPeriodicidade(tipo.periodicidade)})
                    </option>
                  ))}
                </Form.Select>
                {tiposAgendamentoFiltrados.length === 0 && !loadingTipos && (
                  <div className="text-danger mt-2">
                    Nenhum tipo de agendamento disponível. Por favor, cadastre tipos de agendamento antes de continuar.
                  </div>
                )}
              </>
            )}
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Data da Próxima Execução <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={dataProximaExecucao}
                  onChange={(e) => setDataProximaExecucao(e.target.value)}
                  min={formatarDataISO(new Date())}
                  disabled={loading || !camposEditaveis}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Horário <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="time"
                  value={horario}
                  onChange={(e) => setHorario(e.target.value)}
                  disabled={loading || !camposEditaveis}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          {proximaDataDisplay && <div className="mb-3"><em>{proximaDataDisplay}</em></div>}
        </Form>
      </Modal.Body>
      <Modal.Footer className="modal-footer-padrao">
        <div className="modal-footer-acoes-esquerda" />
        <div className="modal-footer-acoes-direita">
          <Button variant="light" className="modal-footer-btn-cancelar" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="success" className="modal-footer-btn-salvar" onClick={handleSave} disabled={loading || loadingTipos || tiposAgendamentoFiltrados.length === 0}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                Salvando...
              </>
            ) : (
              "Salvar"
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

// Estilos globais para modal
document.head.insertAdjacentHTML(
  "beforeend",
  `<style>.modal-90w { width: 90%; max-width: 90%; }</style>`
);

export default AgendamentoDocumentoModal;
